import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  EventType,
  LogLevel,
} from '@azure/msal-browser';
import config from '../config';

/**
 * Single, shared MSAL instance for the whole app.
 *
 * Why a dedicated module (instead of creating the instance inside the React
 * context): both the React tree (AuthContext) AND the non-React API client
 * (api/client.js) need the SAME instance to read/refresh tokens. Previously
 * the API client had its own `msalInstance` variable that was never wired up,
 * so in production it stayed `null` and no Bearer token was ever attached —
 * every authenticated call would 401. Exporting one instance from here makes
 * that impossible.
 *
 * We use the REDIRECT flow (not popup) because:
 *   - It works when third-party cookies / popups are blocked (common in
 *     enterprise browser policies).
 *   - It survives a full page navigation, so "hit the URL → get signed in"
 *     is a first-class flow rather than requiring a user gesture.
 */

// `authConfigured` lets the rest of the app cleanly degrade to an
// unauthenticated (local dev) experience when no client id is provided.
export const authConfigured = Boolean(config.auth.clientId);

export const loginRequest = {
  scopes: config.auth.scopes,
};

const msalConfig = {
  auth: {
    clientId: config.auth.clientId,
    authority: config.auth.authority,
    redirectUri: config.auth.redirectUri,
    postLogoutRedirectUri: config.auth.postLogoutRedirectUri,
    // Ensures MSAL only accepts redirects back to our own app.
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // localStorage so the session survives tab reloads and is shared across
    // tabs; sessionStorage would force a re-login on every new tab.
    cacheLocation: 'localStorage',
    // Persist the transient auth request state in a cookie as well. This is
    // required for the redirect flow to survive Safari / iOS Intelligent
    // Tracking Prevention (ITP) and other browsers that may purge web storage
    // mid-redirect. Without it, users on those browsers can get stuck in a
    // redirect loop or hit "interaction_in_progress" on return from AAD.
    storeAuthStateInCookie: true,
  },
  system: {
    // Renew the access token when it's within this many seconds of expiry,
    // so a request never goes out with a token that expires mid-flight.
    tokenRenewalOffsetSeconds: 300, // 5 minutes
    loggerOptions: {
      logLevel:
        process.env.NODE_ENV === 'production' ? LogLevel.Warning : LogLevel.Info,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        // Route MSAL diagnostics through console; in prod only warnings/errors.
        if (level === LogLevel.Error) console.error('[MSAL]', message);
        else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
        else if (process.env.NODE_ENV !== 'production') console.debug('[MSAL]', message);
      },
    },
  },
};

export const msalInstance = authConfigured
  ? new PublicClientApplication(msalConfig)
  : null;

// Keep the "active account" in sync so acquireTokenSilent has an account to
// use without every caller passing one explicitly.
if (msalInstance) {
  const existing = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();
  if (!existing && accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS) &&
      event.payload?.account
    ) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
}

// Memoize initialization + redirect handling so it runs exactly once even if
// both the API client and AuthContext call it during startup.
let readyPromise = null;

/**
 * Initialize MSAL and process any pending redirect response. Safe to call
 * multiple times — the underlying work happens once.
 *
 * @returns {Promise<import('@azure/msal-browser').AccountInfo | null>}
 *   The signed-in account (if any) after redirect handling.
 */
export function ensureMsalReady() {
  if (!msalInstance) return Promise.resolve(null);
  if (!readyPromise) {
    readyPromise = (async () => {
      await msalInstance.initialize();
      // Completes the sign-in when we return from the AAD redirect.
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
        return result.account;
      }
      return msalInstance.getActiveAccount() || null;
    })();
  }
  return readyPromise;
}

/**
 * Purge a cached account whose session is no longer usable.
 *
 * MSAL keeps AccountInfo records in localStorage independently of token
 * lifetime, so an expired/revoked session still yields an account from
 * getActiveAccount(). Left in place, that account makes the app look signed
 * in (name, email, roles all render) until the first API call fails.
 *
 * Clearing it locally — WITHOUT a logoutRedirect — is deliberate: we only
 * want to forget stale local state, not bounce the user through an AAD
 * sign-out round-trip. If the AAD session itself is still alive, the
 * subsequent login redirect completes silently via SSO.
 *
 * @param {import('@azure/msal-browser').AccountInfo} [account]
 */
export async function clearStaleAccount(account) {
  if (!msalInstance) return;
  try {
    msalInstance.setActiveAccount(null);
    const target = account || msalInstance.getAllAccounts()[0];
    // clearCache accepts an account filter in msal-browser v3+; older
    // versions clear everything, which is an acceptable outcome here.
    if (typeof msalInstance.clearCache === 'function') {
      await msalInstance.clearCache(target ? { account: target } : undefined);
    }
  } catch (err) {
    // Never let cache cleanup break startup — worst case the user sees the
    // sign-in page, which is exactly where we're headed anyway.
    console.warn('Failed to clear stale MSAL account', err);
  }
}

/**
 * Acquire an access token for the API, silently when possible and falling
 * back to an interactive redirect when the refresh token is expired or
 * consent is required.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.interactiveFallback=true]
 *   When true, a silent failure triggers a full-page redirect to re-auth.
 *   Set false for background calls that shouldn't hijack the page.
 * @param {boolean} [opts.forceRefresh=false]
 *   When true, bypass the MSAL token cache and request a brand-new access
 *   token from AAD (using the cached refresh token). Used to recover from a
 *   401/403 caused by a token the server considers stale/revoked.
 * @returns {Promise<string|null>} The access token, or null if unavailable.
 */
export async function acquireApiToken({
  interactiveFallback = true,
  forceRefresh = false,
} = {}) {
  if (!msalInstance) return null;
  await ensureMsalReady();

  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh,
    });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError && interactiveFallback) {
      // Full-page redirect to re-authenticate. The current URL is preserved
      // by MSAL and restored after the round-trip.
      await msalInstance.acquireTokenRedirect(loginRequest);
      return null; // navigation is happening; nothing more to do here.
    }
    console.error('Token acquisition failed', err);
    return null;
  }
}
